import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { Button, Field, Modal } from '../../settings/kit'
import type { OfApi } from '../../ofApi'
import {
  copyText,
  decodeEntities,
  guestRequest,
  isLinkExpired,
  isSigned,
  linkBody,
  LINKS_PATH,
  linkUrl,
  NAME_MAX,
  nameUnchanged,
  REFERER,
  RENAME_PATH,
  renameBody,
  toItem,
  type RfItem,
} from './releaseFormsData'
import { copyToClipboard } from './copy'

/*
 * The release-form dialogs of onlyfans.com: ModalReleaseFormsGuest (invite /
 * edit a guest tag), ModalReleaseFormsLink (create / edit a release form link),
 * ModalReleaseFormsDocs (rename an uploaded release form), ModalLinkOrDocs and
 * ModalAddReleaseFormsTo (the two small menus).
 */

const message = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback)

/** A grey line saying the action happens on onlyfans.com, with a link there. */
export function OnOnlyFans({ text }: { text: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  return (
    <p className="ofr-of-note">
      {text}{' '}
      <a href={REFERER} target="_blank" rel="noopener noreferrer">
        {t('onOnlyFans.open')}
        <FeatherIcon name="external-link" size={12} />
      </a>
    </p>
  )
}

/** The blue link block of the guest and link dialogs (click copies it). */
function LinkBlock({ url, valid, onCopy }: { url: string; valid: string; onCopy: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  return (
    <div className="ofr-linkblock">
      <p className="ofr-linkblock-text">{t('provideLink')}</p>
      <button type="button" className="ofr-linkblock-url" onClick={onCopy}>
        {url}
      </button>
      <p className="ofr-linkblock-note">{valid}</p>
      <p className="ofr-linkblock-note">{t('verificationMinutes')}</p>
    </div>
  )
}

interface Common {
  of: OfApi
  canWrite: boolean
  onClose: () => void
  say: (text: string) => void
}

/**
 * Invite a user who has no account yet (POST guests/ {name}) or rename a guest
 * tag (PATCH guests/<id> {name}); then the invitation link to copy.
 */
export function GuestModal({ of, canWrite, onClose, say, item, onSaved, onSearchUsers }: Common & { item: RfItem | null; onSaved: (saved: RfItem, created: boolean) => void; onSearchUsers: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const oldName = item?.name ?? ''
  const [name, setName] = useState(decodeEntities(oldName))
  const [token, setToken] = useState(item?.token ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const disabled = !canWrite || nameUnchanged(name, oldName) || busy
  const title = oldName ? t('guestModal.titleEdit') : token ? t('guestModal.titleAdded') : t('guestModal.titleInvite')
  const showForm = !token || !!oldName

  const save = async () => {
    if (disabled) return
    setBusy(true)
    setError('')
    try {
      const req = guestRequest(name.trim(), item?.id)
      const res = toItem(await of.send(req.method, req.path, REFERER, req.body), 'guest')
      if (res?.token) {
        setToken(res.token)
        onSaved({ ...res, type: 'guest' }, !item)
        if (oldName) onClose()
      }
    } catch (e) {
      setError(message(e, t('error')))
    } finally {
      setBusy(false)
    }
  }
  const copy = async () => {
    if (!token) return
    await copyToClipboard('guest', { token }, say, t)
    onClose()
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          {oldName && (
            <Button variant="flat" onClick={() => void copy()}>
              <FeatherIcon name="share-2" size={16} /> {t('menu.copyLink')}
            </Button>
          )}
          {busy && <Spinner size={18} />}
          <Button variant="flat" onClick={onClose}>
            {t('cancel')}
          </Button>
          {showForm ? (
            <Button variant="flat" disabled={disabled} onClick={() => void save()}>
              {t('save')}
            </Button>
          ) : (
            <Button variant="flat" onClick={() => void copy()}>
              {t('menu.copyLink')}
            </Button>
          )}
        </>
      }
    >
      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <Field label={t('name')} value={name} onChange={(v) => setName(v.slice(0, NAME_MAX))} maxLength={NAME_MAX} autoFocus disabled={!canWrite} />
        </form>
      )}
      {token && <LinkBlock url={copyText('guest', { token }).text} valid={t('guestModal.valid')} onCopy={() => void copy()} />}
      {error && <p className="oset-field-error">{error}</p>}
      <button type="button" className="ofr-text-link" onClick={onSearchUsers}>
        {t('guestModal.hasAccount')}
      </button>
    </Modal>
  )
}

/**
 * Create a release form link (POST release-form-links {name}) and show it, or
 * rename one (PATCH release-forms/rename {entityId, name}).
 */
export function LinkModal({ of, canWrite, onClose, say, item, onSaved }: Common & { item: RfItem | null; onSaved: (saved: RfItem, created: boolean) => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const oldName = item?.name ?? ''
  const [name, setName] = useState(decodeEntities(oldName))
  const [link, setLink] = useState<RfItem | null>(item)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const disabled = !canWrite || nameUnchanged(name, oldName) || busy
  const closed = !!link && (isLinkExpired(link) || isSigned(link))
  const title = oldName ? t('linkModal.titleEdit') : link ? t('linkModal.titleLink') : t('linkModal.titleCreate')
  const showForm = !link || !!oldName

  const save = async () => {
    if (disabled) return
    setBusy(true)
    setError('')
    try {
      const trimmed = name.trim()
      if (item) {
        const res = toItem(await of.send('PATCH', RENAME_PATH, REFERER, renameBody(item.id, trimmed)), item.type)
        onSaved({ ...item, ...(res ?? {}), name: res?.name || trimmed }, false)
        onClose()
      } else {
        const res = toItem(await of.send('POST', LINKS_PATH, REFERER, linkBody(trimmed)), 'link')
        if (res) {
          const created = { ...res, type: 'link' }
          setLink(created)
          onSaved(created, true)
        }
      }
    } catch (e) {
      setError(message(e, t('error')))
    } finally {
      setBusy(false)
    }
  }
  const copy = async () => {
    if (!link) return
    await copyToClipboard('link', { code: link.code, signature: link.signature }, say, t)
    onClose()
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          {busy && <Spinner size={18} />}
          {!closed && oldName && (
            <Button variant="flat" onClick={() => void copy()}>
              <FeatherIcon name="share-2" size={16} /> {t('menu.copyLink')}
            </Button>
          )}
          <Button variant="flat" onClick={onClose}>
            {t('cancel')}
          </Button>
          {showForm ? (
            <Button variant="flat" disabled={disabled} onClick={() => void save()}>
              {t('save')}
            </Button>
          ) : (
            <Button variant="flat" onClick={() => void copy()}>
              {t('menu.copyLink')}
            </Button>
          )}
        </>
      }
    >
      {showForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <Field label={t('linkModal.linkName')} value={name} onChange={(v) => setName(v.slice(0, NAME_MAX))} maxLength={NAME_MAX} autoFocus disabled={!canWrite} />
        </form>
      )}
      {link && !closed && <LinkBlock url={linkUrl(link.code, link.signature)} valid={t('linkModal.valid')} onCopy={() => void copy()} />}
      {error && <p className="oset-field-error">{error}</p>}
    </Modal>
  )
}

/**
 * An uploaded release form: rename it (PATCH release-forms/rename). Adding a
 * new one uploads ID documents through OnlyFans' own uploader, so that part
 * is onlyfans.com only.
 */
export function DocsModal({ of, canWrite, onClose, item, onSaved }: Omit<Common, 'say'> & { item: RfItem | null; onSaved: (saved: RfItem) => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const oldName = item?.name ?? ''
  const [name, setName] = useState(decodeEntities(oldName))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const disabled = !item || !canWrite || nameUnchanged(name, oldName) || busy

  const save = async () => {
    if (disabled || !item) return
    setBusy(true)
    setError('')
    try {
      const trimmed = name.trim()
      const res = toItem(await of.send('PATCH', RENAME_PATH, REFERER, renameBody(item.id, trimmed)), item.type)
      onSaved({ ...item, ...(res ?? {}), name: res?.name || trimmed })
      onClose()
    } catch (e) {
      setError(message(e, t('error')))
      setBusy(false)
    }
  }

  return (
    <Modal
      title={oldName ? t('docsModal.titleEdit') : t('docsModal.titleAdd')}
      onClose={onClose}
      actions={
        <>
          {busy && <Spinner size={18} />}
          <Button variant="flat" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button variant="flat" disabled={disabled} onClick={() => void save()}>
            {t('save')}
          </Button>
        </>
      }
    >
      {oldName ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <Field label={t('name')} value={name} onChange={(v) => setName(v.slice(0, NAME_MAX))} maxLength={NAME_MAX} autoFocus disabled={!canWrite} />
        </form>
      ) : (
        <OnOnlyFans text={t('onOnlyFans.upload')} />
      )}
      {error && <p className="oset-field-error">{error}</p>}
    </Modal>
  )
}

/** A menu dialog: OnlyFans' list of icon + label buttons in a small modal. */
function MenuModal({ onClose, children, note }: { onClose: () => void; children: ReactNode; note?: ReactNode }) {
  return (
    <Modal onClose={onClose}>
      <div className="ofr-menu-modal">{children}</div>
      {note}
    </Modal>
  )
}

/** ModalLinkOrDocs: shown instead of the link dialog when the account may upload release forms. */
export function ChooserModal({ onClose, onLink, canWrite }: { onClose: () => void; onLink: () => void; canWrite: boolean }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  return (
    <MenuModal onClose={onClose} note={<OnOnlyFans text={t('onOnlyFans.upload')} />}>
      <button type="button" className="ofr-menu-item" onClick={onLink} disabled={!canWrite}>
        <FeatherIcon name="link" size={20} className="ofr-menu-icon" />
        {t('chooser.byLink')}
      </button>
      <button type="button" className="ofr-menu-item" disabled>
        <FeatherIcon name="file-text" size={20} className="ofr-menu-icon" />
        {t('chooser.upload')}
      </button>
    </MenuModal>
  )
}

/** ModalAddReleaseFormsTo: where the selection goes. OnlyFans hands it to its own composer, so these are onlyfans.com only. */
export function AddToModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'releaseFormsPage' })
  const items: [string, string][] = [
    ['plus-square', t('addToMenu.post')],
    ['external-link', t('addToMenu.newTab')],
    ['message-square', t('addToMenu.message')],
  ]
  return (
    <MenuModal onClose={onClose} note={<OnOnlyFans text={t('onOnlyFans.addTo')} />}>
      {items.map(([icon, label], i) => (
        <button key={label} type="button" className={`ofr-menu-item ${i ? 'has-divider' : ''}`} disabled>
          <FeatherIcon name={icon} size={20} className="ofr-menu-icon" />
          {label}
        </button>
      ))}
    </MenuModal>
  )
}

/** A small popover menu (OnlyFans' v-menu dropdown): closes on a click outside or Escape. */
export function Popover({
  button,
  label,
  children,
  className,
  disabled,
  active,
}: {
  button: ReactNode
  label: string
  children: (close: () => void) => ReactNode
  className?: string
  disabled?: boolean
  active?: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={ref} className={`ofr-pop ${className ?? ''}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`ofr-icon-btn ${open || active ? 'is-active' : ''}`}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault()
          setOpen((o) => !o)
        }}
      >
        {button}
      </button>
      {open && (
        <div className="ofr-pop-menu" role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}
